import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { convertDaysLeft } from "@/lib/date";
import { cn } from "@/lib/utils";
import { AdminTeamDTO, AdminTeamUserDTO } from "@shaarp/common";
import { Column, ColumnDef } from "@tanstack/react-table";
import { ChevronDownIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { RiExternalLinkFill } from "react-icons/ri";
import { Link } from "react-router-dom";
import AvatarCompany from "../company/avatar-company";
import { useToastError } from "../ui/use-toast";
import DataTableCellAdminAdvisor from "./data-table-cell-admin-advisor";
import DataTableCellAdminInstruction from "./data-table-cell-admin-instruction";
import DataTableCellAdminReferral from "./data-table-cell-admin-referral";
import DataTableCellAdminsActions from "./data-table-cell-admins-actions";
import DataTableCellAdminsBilling from "./data-table-cell-admins-billing";
import DataTableCellAdminsDiscounts from "./data-table-cell-admins-discounts";
import DataTableCellAdminsFeatureFlags from "./data-table-cell-admins-feature-flags";
import DataTableCellAdminsLicenseInventory from "./data-table-cell-admins-license-inventory";
import DataTableCellAdminsNextBilling from "./data-table-cell-admins-nextbilling";
import DataTableCellAdminsSurvey from "./data-table-cell-admins-survey";
import { DialogAdminTeamUsers } from "./dialog-admin-team-users";
import { DialogManageCredits } from "./dialog-manage-credits";

export const dataTableAdminTeamsColumns = () => {
  const { t, currentLanguage } = useTranslation();
  const api = useApi();
  const toastError = useToastError();

  const [users, setUsers] = useState<AdminTeamUserDTO[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data } = await api.get<AdminTeamUserDTO[]>("/admin/users");
      setUsers(data);
    } catch (error) {
      toastError();
    } finally {
      setLoading(false);
    }
  };

  return [
    {
      id: "Status",
      accessorFn: (team) => team.status,
      enableHiding: false,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.status")}
        />
      ),
      cell: ({ row }) => {
        const team: AdminTeamDTO = row.original;
        const getStatusVariant = (status: string) => {
          switch (status) {
            case "enabled":
              return "success";
            case "disabled":
              return "warning";
            case "archived":
              return "outline";
            default:
              return "outline";
          }
        };

        const getStatusText = (status: string) => {
          switch (status) {
            case "enabled":
              return t("admin.dataTableAdminTeams.badges.enabled");
            case "disabled":
              return t("admin.dataTableAdminTeams.badges.disabled");
            case "archived":
              return t("admin.dataTableAdminTeams.badges.archived");
            default:
              return t("admin.dataTableAdminTeams.badges.disabled");
          }
        };

        return (
          <Badge variant={getStatusVariant(team.status)}>
            {getStatusText(team.status)}
          </Badge>
        );
      },
    },
    {
      id: "Nom",
      accessorFn: (team) => team.name,
      enableHiding: false,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.name")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        return <span className="font-bold">{team.name}</span>;
      },
    },
    {
      id: "Date de création",
      accessorFn: (team) => new Date(team.createdAt),
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.createdAt")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        return <span>{new Date(team.createdAt).toLocaleString(currentLanguage)}</span>;
      },
    },
    {
      id: "Création",
      accessorFn: (team) => team.setupType,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.creation")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        return <DataTableCellAdminsSurvey team={team} />;
      },
    },
    {
      id: "Facturation",
      accessorFn: (team) => team.billingEnabled,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.billing")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        return <DataTableCellAdminsBilling team={team} />;
      },
    },
    {
      id: "Abonnement",
      // The cell shows a license breakdown, so sorting must rank on the total
      // seat count — ranking on "has a subscription" left every subscriber tied.
      accessorFn: (team) =>
        (team.purchasedProMonthly ?? 0) + (team.purchasedProAnnual ?? 0) ||
        // Teams still in setup hold no stock yet: rank them on the planned
        // starting seats the cell displays instead.
        (team.initialProLicenseCountAnnual ??
          team.initialProLicenseCountMonthly ??
          0),
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.subscription")}
        />
      ),
      cell: ({ row }) => (
        <DataTableCellAdminsLicenseInventory team={row.original} />
      ),
    },
    {
      id: "Feature flags",
      accessorFn: (team) =>
        [
          team.featureContactDirectEnabled,
          team.featureContactRevealEmailEnabled,
          team.featureContactRevealPhoneEnabled,
          team.featureReportingWeeklyActivityEnabled,
          team.featureNewNavigationEnabled,
        ].filter(Boolean).length,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.featureFlags")}
        />
      ),
      cell: ({ row }) => (
        <DataTableCellAdminsFeatureFlags team={row.original} />
      ),
    },
    {
      id: "Crédits",
      accessorFn: (team) => team.creditsBalance,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.credits")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        const [giftDialogOpen, setGiftDialogOpen] = useState(false);

        return (
          <>
            <div
              className="flex gap-1 items-center cursor-pointer"
              onClick={() => setGiftDialogOpen(true)}
            >
              <Badge variant="outline">
                {team.creditsBalance}
              </Badge>
              <Badge variant="secondary">
                {team.apiCreditsBalance} API
              </Badge>
              <ChevronDownIcon className="w-3 h-3" />
            </div>

            {giftDialogOpen && (
              <DialogManageCredits
                team={team}
                open={giftDialogOpen}
                onClose={() => setGiftDialogOpen(false)}
              />
            )}
          </>
        );
      },
    },
    {
      id: "Réduction",
      accessorFn: (team) => team.monthlyDiscount || team.annualDiscount,
      header: ({ column }: { column: Column<AdminTeamDTO> }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.discount")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        return <DataTableCellAdminsDiscounts team={team} />;
      },
    },
    {
      id: "Prélèvement",
      accessorFn: (team) => team.stripeMonthlySubscriptionCurrentPeriodEndsAt,
      header: ({ column }: { column: Column<AdminTeamDTO> }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.nextBilling")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        return <DataTableCellAdminsNextBilling team={team} />;
      },
    },
    {
      id: "Méth. paiement",
      accessorFn: (team) => team.stripePaymentMethodeExist,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.paymentMethod")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        if (team.stripePaymentMethodeExist === null) {
          return;
        }

        return (
          <div className="flex flex-row gap-2">
            <Badge
              variant={
                team.status === "enabled"
                  ? team.paymentMethodRequired
                    ? "success"
                    : "warning"
                  : "outline"
              }
              className="whitespace-nowrap"
            >
              {team.paymentMethodRequired
                ? t("admin.dataTableAdminTeams.badges.required")
                : t("admin.dataTableAdminTeams.badges.notRequired")}
            </Badge>
            <Badge
              variant={
                !team.billingEnabled
                  ? "outline"
                  : team.stripePaymentMethodeExist
                  ? "success"
                  : "destructive"
              }
            >
              {team.stripePaymentMethodeExist
                ? t("admin.dataTableAdminTeams.badges.ok")
                : t("admin.dataTableAdminTeams.badges.missing")}
            </Badge>
          </div>
        );
      },
    },
    {
      id: "Email facturation",
      accessorFn: (team) => team.billingEmail,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.billingEmail")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        if (team.billingEmail !== null) {
          return team.billingEmail;
        }

        return (
          <Badge variant={!team.billingEnabled ? "outline" : "destructive"}>
            {t("admin.dataTableAdminTeams.badges.missing")}
          </Badge>
        );
      },
    },
    {
      id: "Période d'essai",
      accessorFn: (team) =>
        team.trialEndsAt ? new Date(team.trialEndsAt) : null,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.trialPeriod")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;

        if (team.trialCountDays) {
          return (
            <Badge variant="warning">
              {t("admin.dataTableAdminTeams.badges.pending", {
                days: team.trialCountDays,
              })}
            </Badge>
          );
        } else if (team.trialEndsAt) {
          const trialStartedAt = team.trialStartedAt
            ? new Date(team.trialStartedAt)
            : null;
          const trialEndsAt = new Date(team.trialEndsAt);
          const daysLeft = convertDaysLeft(trialEndsAt);

          return (
            <Tooltip>
              <TooltipTrigger>
                <Badge variant={daysLeft < 7 ? "warning" : "outline"}>
                  {t("admin.dataTableAdminTeams.badges.daysLeft", {
                    days: daysLeft,
                  })}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                {trialStartedAt
                  ? t("admin.dataTableAdminTeams.tooltips.trialPeriod", {
                      start: trialStartedAt.toLocaleDateString(currentLanguage),
                      end: trialEndsAt.toLocaleDateString(currentLanguage),
                    })
                  : t("admin.dataTableAdminTeams.tooltips.trialEnd", {
                      end: trialEndsAt.toLocaleDateString(currentLanguage),
                    })}
              </TooltipContent>
            </Tooltip>
          );
        }

        return <></>;
      },
    },
    {
      id: "Société liée",
      accessorFn: (team) => !!team.companyLinked,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.linkedCompany")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        const company = team.companyLinked;
        if (!company) {
          return (
            <Badge
              variant={team.status === "enabled" ? "destructive" : "outline"}
            >
              {t("admin.dataTableAdminTeams.badges.missing")}
            </Badge>
          );
        }

        return (
          <Button variant="outline" className={cn("p-2")}>
            <Link
              to={`/company/${company?.id}`}
              target="_blank"
              className="flex items-center text-xs"
            >
              <AvatarCompany
                className="mr-2 w-4 h-4 cursor-pointer"
                company={company}
              />
              {company?.id
                ? company?.name ||
                  t("admin.dataTableAdminTeams.company.unknownName")
                : t("admin.dataTableAdminTeams.company.notSelected")}
              <RiExternalLinkFill className="ml-2" />
            </Link>
          </Button>
        );
      },
    },
    {
      id: "Société liée vérifiée",
      accessorFn: (team) => !!team.companyLinked?.isVerified,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.linkedCompanyVerified")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        const company = team.companyLinked;
        if (!company) {
          return;
        } else if (!company.isVerified) {
          return (
            <Badge
              variant={team.status === "enabled" ? "destructive" : "outline"}
            >
              {t("admin.dataTableAdminTeams.badges.notVerified")}
            </Badge>
          );
        }

        return (
          <Badge variant="success">
            {t("admin.dataTableAdminTeams.badges.yes")}
          </Badge>
        );
      },
    },
    {
      id: "Instruction IA",
      accessorFn: (team) => team.instructionCoverageScore ?? undefined,
      sortUndefined: "last",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.aiInstruction")}
        />
      ),
      cell: DataTableCellAdminInstruction,
    },
    {
      id: "Nb utilisateurs",
      accessorFn: (team) => team.usersCount,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.usersCount")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        const [open, setOpen] = useState(false);

        return (
          <>
            <Tooltip>
              <TooltipTrigger>
                <Badge
                  variant="outline"
                  onClick={() => setOpen(true)}
                  className="cursor-pointer"
                >
                  {team.usersCount}
                  <ChevronDownIcon className="ml-2 w-3 h-3" />
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                {t("admin.dataTableAdminTeams.tooltips.usersCount", {
                  count: team.usersCount,
                })}
              </TooltipContent>
            </Tooltip>

            {open && (
              <DialogAdminTeamUsers
                team={team}
                open={open}
                onClose={() => setOpen(false)}
              />
            )}
          </>
        );
      },
    },
    {
      id: "Nb utili. instruction",
      accessorFn: (team) => team.usersWithInstructionCount,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t(
            "admin.dataTableAdminTeams.columns.usersWithInstructionCount"
          )}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;

        return (
          <Badge
            variant={
              team.usersWithInstructionCount === team.usersCount
                ? "success"
                : "warning"
            }
          >
            {team.usersWithInstructionCount}
          </Badge>
        );
      },
    },
    {
      id: "Nb invitations",
      accessorFn: (team) => team.invitationCount,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.invitationCount")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        if (team.invitationCount === null) {
          return "?";
        }

        return team.invitationCount;
      },
    },
    {
      id: "Parrain",
      accessorFn: (team) => team.invitedBy,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.invitedBy")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        return (
          <DataTableCellAdminReferral
            team={team}
            users={users}
            loading={loading}
          />
        );
      },
    },
    {
      id: "Actif dernier 24h",
      accessorFn: (team) => team.usersActiveLastDayCount,
      initialVisibility: false,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.activeLastDay")}
        />
      ),
    },
    {
      id: "Actif dernier 7j",
      accessorFn: (team) => team.usersActiveLastWeekCount,
      initialVisibility: false,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.activeLastWeek")}
        />
      ),
    },
    {
      id: "Advisor",
      accessorFn: (team) => team.advisorUserId,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.advisor")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;
        return (
          <DataTableCellAdminAdvisor
            team={team}
            users={users}
            loading={loading}
          />
        );
      },
    },
    {
      id: "Actions",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("admin.dataTableAdminTeams.columns.actions")}
        />
      ),
      cell: ({ row }) => {
        const team = row.original;

        return <DataTableCellAdminsActions team={team} />;
      },
    },
  ] as ColumnDef<AdminTeamDTO>[];
};
